import * as React from 'react'
import { useRef, useState } from 'react'
import {
    Container as RSContainer,
    OnDropCallback
} from '@richardrout/react-smooth-dnd'

import { ListType, ItemType, NewItemButtonProps, Position } from '../dtos'

import { DragDropListsContainer } from '../hooks'
import { ItemDraggableContainer } from '../item/Item'
import { NewItemDefault } from '../item/NewItem'
import { NewItemButtonDefault } from '../item/NewItemButton'

import * as styles from './List.module.less'
import * as scrollbarStyles from '../scrollbar-styles/FancyScrollbar.module.less'

export interface LaneProps {
    list: ListType
    Header?: JSX.Element
    items: ItemType[]
    filter: {
        isFiltered: boolean
        visibleItems?: string[]
    }
}

export const Lane: React.FC<LaneProps> = (props) => {
    const { list, Header, items, filter } = props
    const refListStart = useRef()
    const refListEnd = useRef()
    const {
        components,
        showNewItemListId,
        showScrollbar,
        toggleShowNewItem
    } = DragDropListsContainer.useContainer()
    const NewItemButton = components?.NewItemButton || NewItemButtonDefault
    const showNewItem = list.id === showNewItemListId
    const [newItemPosition, setNewItemPosition] = useState<Position>('end')
    const scrollbarClassName = getScrollbarClassName(showScrollbar)

    const createNewItemButtonProps: CreateNewItemButtonProps = {
        NewItemButton,
        listId: list.id,
        isDisabled: showNewItem,
        refListEnd,
        refListStart,
        setNewItemPosition,
        showNewItem,
        toggleShowNewItem
    }

    return (
        <div className={`${styles.listWrapper} ${scrollbarClassName}`}>
            <div className={`${styles.list} ${scrollbarClassName}`}>
                {Header}
                <CreateNewItemButton {...createNewItemButtonProps} />
                <ItemListInner
                    list={list}
                    items={items}
                    filter={filter}
                    newItemPosition={newItemPosition}
                    refListEnd={refListEnd}
                    refListStart={refListStart}
                    scrollbarClassName={scrollbarClassName}
                />
                {items.length > 0 && (
                    <CreateNewItemButton
                        {...createNewItemButtonProps}
                        position="end"
                    />
                )}
            </div>
        </div>
    )
}

interface CreateNewItemButtonProps {
    listId: string
    isDisabled: boolean
    NewItemButton: React.ElementType<any>
    position?: Position
    refListStart: React.MutableRefObject<undefined>
    refListEnd: React.MutableRefObject<undefined>
    setNewItemPosition: React.Dispatch<React.SetStateAction<Position>>
    showNewItem: boolean
    toggleShowNewItem: (listId: any) => void
}

const CreateNewItemButton: React.FC<CreateNewItemButtonProps> = (props) => {
    const {
        listId,
        NewItemButton,
        refListEnd,
        refListStart,
        setNewItemPosition,
        showNewItem,
        toggleShowNewItem
    } = props

    const position: Position =
        props.position !== undefined ? props.position : 'start'

    const onNewItemClicked = (position: Position) => {
        toggleShowNewItem(listId)
        setNewItemPosition(position)
        let scrollToDiv: HTMLElement = refListEnd.current as any
        let top: boolean = true
        if (position === 'start') {
            scrollToDiv = refListStart.current as any
            top = false
        }
        setTimeout(() => {
            scrollToDiv.scrollIntoView(top)
        }, 0)
    }

    const onNewItemClickedStartProps: NewItemButtonProps = {
        isDisabled: showNewItem,
        onNewItemClicked,
        position
    }

    return <NewItemButton {...onNewItemClickedStartProps} />
}

interface ItemListProps {
    list: ListType
    items: ItemType[]
    filter: {
        isFiltered: boolean
        visibleItems?: string[]
    }
    newItemPosition: Position
    refListStart: React.MutableRefObject<any>
    refListEnd: React.MutableRefObject<any>
    scrollbarClassName: string
}

interface ItemListInnerProps extends ItemListProps {
    droppableProvidedPlaceholder?: any
}

const ItemListInner: React.FC<ItemListInnerProps> = (props) => {
    const {
        list,
        droppableProvidedPlaceholder,
        items,
        newItemPosition,
        refListEnd,
        refListStart,
        scrollbarClassName,
        filter
    } = props
    const {
        classNameItem,
        components,
        extraProps,
        onDropItem: onDrop,
        onItemClick,
        onItemDelete,
        onSaveNewItemClicked,
        showNewItemListId,
        toggleShowNewItem
    } = DragDropListsContainer.useContainer()
    const showNewItem = list.id === showNewItemListId
    const Item = components?.Item
    const NewItem = components?.NewItem || NewItemDefault

    const visibleItems = filter.visibleItems || []

    const onSaveNewItem = (data: any) => {
        if (onSaveNewItemClicked) {
            const fakeDataToImprove = {
                listId: list.id,
                data,
                index: newItemPosition === 'start' ? 0 : items.length
            }
            // console.log('onSaveNewItem', fakeDataToImprove)
            onSaveNewItemClicked(fakeDataToImprove)

            // this allows to keep creating new tasks until users Escapes with kboard or cancels with button.
            toggleShowNewItem('')
            toggleShowNewItem(list.id)
        }
    }

    const onCancelNewItem = () => {
        toggleShowNewItem('')
    }

    const handleOnItemDelete = (id: string) => {
        onItemDelete({ id, listId: list.id })
    }

    const onDropItem: OnDropCallback = (e) => {
        onDrop(list.id, e)
    }

    const itemList = (
        // React.useMemo(() => { return (
        <RSContainer
            disableScrollOverlapDetection={true}
            autoScrollEnabled={true}
            {...props.list}
            groupName="col"
            onDrop={onDropItem}
            getChildPayload={(index: number) => list.itemIds[index]}
            dragClass={styles.cardGhost}
            dropClass={styles.cardGhostDrop}
            dropPlaceholder={{
                animationDuration: 150,
                showOnTop: true,
                className: styles.dropPreview
                // className: styles.dropPreview
            }}
            // dropPlaceholderAnimationDuration={200}
        >
            {items.map((item, index) => {
                // console.log('state', items, item)
                const isVisible = filter.isFiltered
                    ? Boolean(
                          visibleItems.find((visibleItem) => {
                              return visibleItem === item.id
                          })
                      )
                    : true

                return (
                    <ItemDraggableContainer
                        key={item.id}
                        index={index}
                        isVisible={isVisible}
                        classNameItem={classNameItem}
                        events={{
                            onItemClick,
                            onItemDelete: handleOnItemDelete
                        }}
                        Item={Item}
                        {...item}
                        extraProps={extraProps}
                    />
                )
            })}
        </RSContainer>
    )

    //  )}, [items, Item])

    const newItemProps = {
        onCancelNewItem,
        onSaveNewItem
    }

    const newItemTop = showNewItem && newItemPosition === 'start' && (
        <NewItem {...newItemProps} />
    )
    const newItemBottom = showNewItem && newItemPosition === 'end' && (
        <NewItem {...newItemProps} />
    )
    const taskListClassName = getTaskListClassName(
        list.itemIds.length,
        scrollbarClassName
    )

    return (
        <div className={`${taskListClassName} ${scrollbarClassName}`}>
            {newItemTop}
            <div ref={refListStart} />
            {itemList}
            {droppableProvidedPlaceholder}
            {newItemBottom}
            <div ref={refListEnd} />
        </div>
    )
}

const getScrollbarClassName = (showScrollbar: boolean): string => {
    return showScrollbar ? styles.fancyScrollbar : styles.disableScrollbars
}

const getTaskListClassName = (
    itemListCount: number,
    scrollbarClassName: string
): string => {
    let taskListClassName = `${styles.itemList} ${scrollbarClassName}`
    return itemListCount > 0
        ? `${taskListClassName} ${styles.itemListWithItems}`
        : `${taskListClassName} ${styles.itemListNoItems}`
}
